import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Drivers from './pages/Drivers';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Navigation from './components/Navigation';
import DriverProfile from './pages/DriverProfile';
import OwnerProfile from './pages/OwnerProfile';
import RaceMatrix from './pages/RaceMatrix';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import PublicTeamPage from './pages/PublicTeamPage';
import { RegistrationsProvider } from './context/RegistrationsContext';
import { SettingsProvider, applyBranding } from './context/SettingsContext';
import { LangProvider, useLang } from './context/LangContext';
import { useT } from './i18n';
import { usePushNotifications, enablePushNotifications, isPwa } from './hooks/usePushNotifications';
import OnboardingWizard from './components/OnboardingWizard';
import JoinTeamPage from './pages/JoinTeamPage';
import PastSeasons from './pages/PastSeasons';
import './App.css';

import { CATEGORIES as CATS, CAT_STYLE as CAT_STYLE_MAP } from './categories';
const CAT_C = Object.fromEntries(Object.entries(CAT_STYLE_MAP).map(([k, v]) => [k, v.color]));

// Full profile form for a self-registered driver not yet linked to a team
function PendingDriverProfile({ userInfo, token, onJoinTeam }) {
  const [form, setForm] = useState({
    name: userInfo?.name || '',
    dob: '',
    phone: '',
    emergency_phone: '',
    address: '',
    license_number: '',
    uci_id: '',
    category: 'Elite',
    photo_url: '',
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [publicId,  setPublicId]  = useState(null);
  const [msg,    setMsg]    = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Load existing account data on mount
  useEffect(() => {
    axios.get('/api/auth/driver-account', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const d = res.data;
        setForm(f => ({
          ...f,
          name:            d.name            || f.name,
          dob:             d.dob             ? d.dob.slice(0, 10) : '',
          phone:           d.phone           || '',
          emergency_phone: d.emergency_phone || '',
          address:         d.address         || '',
          license_number:  d.license_number  || '',
          uci_id:          d.uci_id          || '',
          category:        d.category        || 'Elite',
          photo_url:       d.photo_url       || '',
        }));
        if (d.photo_url) setPhotoPreview(d.photo_url);
        if (d.public_id) setPublicId(d.public_id);
      })
      .catch(() => {});
  }, [token]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 400;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      URL.revokeObjectURL(url);
      setPhotoPreview(dataUrl);
      set('photo_url', dataUrl);
    };
    img.src = url;
  };

  const formField = (label, key, type = 'text', placeholder = '', required = false) => (
    <div key={key} style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', marginBottom: 7, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#849495' }}>
        {label}{required && <span style={{ color: 'var(--brand-primary)', marginLeft: 4 }}>*</span>}
      </label>
      <input
        type={type} value={form[key]} required={required} placeholder={placeholder}
        onChange={e => set(key, e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(59,73,75,0.6)', color: '#e2e2e8', fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none' }}
        onFocus={e => e.target.style.borderBottomColor = 'var(--brand-primary)'}
        onBlur={e  => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)'}
      />
    </div>
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      await axios.put('/api/auth/driver-account', form, { headers: { Authorization: `Bearer ${token}` } });
      setMsg({ type: 'success', text: 'Profil gespeichert ✓' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Fehler beim Speichern' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const initials = form.name ? form.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '??';

  // ── Team search state ──
  const [teams,          setTeams]          = useState([]);
  const [teamSearch,     setTeamSearch]     = useState('');
  const [myRequests,     setMyRequests]     = useState([]);
  const [myInvites,      setMyInvites]      = useState([]);
  const [joinLoading,    setJoinLoading]    = useState(null); // team_id being submitted
  const [withdrawLoading, setWithdrawLoading] = useState(null); // request id being withdrawn
  const [inviteActionLoading, setInviteActionLoading] = useState(null); // invite id being actioned
  const [activeTab,      setActiveTab]      = useState('profile'); // 'profile' | 'team'

  const loadMyRequests = () =>
    axios.get('/api/drivers/join-request/mine', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMyRequests(res.data))
      .catch(() => {});

  const loadMyInvites = () =>
    axios.get('/api/drivers/invites/mine', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMyInvites(res.data))
      .catch(err => console.error('[invites/mine]', err?.response?.status, err?.response?.data?.error || err.message));

  // Load all teams + own requests + invites on mount
  useEffect(() => {
    axios.get('/api/drivers/teams/search')
      .then(res => setTeams(res.data))
      .catch(() => {});
    loadMyRequests();
    loadMyInvites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Poll every 15s — detect when admin approved a join request or added driver manually
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get('/api/auth/driver-status', { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.driver_id) {
          if (onJoinTeam) onJoinTeam({
            driverId:  res.data.driver_id,
            id:        res.data.driver_id,
            public_id: res.data.team_public_id,
            token:     res.data.token || null,
          });
        }
      } catch (_) {}
    }, 15000);
    return () => clearInterval(interval);
  }, [token, onJoinTeam]);

  const handleInviteAction = async (inviteId, action) => {
    setInviteActionLoading(inviteId);
    try {
      const res = await axios.post(`/api/drivers/invites/${inviteId}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (action === 'accept') {
        // Update userInfo in place — no logout/reload needed
        if (onJoinTeam) {
          onJoinTeam({
            driverId:  res.data.driver_id,
            id:        res.data.team_id,
            public_id: res.data.team_public_id,
            token:     res.data.token || null,
          });
        }
      } else {
        await loadMyInvites();
        setMsg({ type: 'success', text: 'Einladung abgelehnt' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Fehler' });
    } finally {
      setInviteActionLoading(null);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const handleJoinRequest = async (teamId) => {
    setJoinLoading(teamId);
    try {
      await axios.post('/api/drivers/join-request', { team_id: teamId }, { headers: { Authorization: `Bearer ${token}` } });
      await loadMyRequests();
      setMsg({ type: 'success', text: 'Anfrage gesendet ✓' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Fehler beim Senden' });
    } finally {
      setJoinLoading(null);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const handleWithdraw = async (requestId) => {
    setWithdrawLoading(requestId);
    try {
      await axios.delete(`/api/drivers/join-request/${requestId}`, { headers: { Authorization: `Bearer ${token}` } });
      await loadMyRequests();
      setMsg({ type: 'success', text: 'Anfrage zurückgezogen' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Fehler' });
    } finally {
      setWithdrawLoading(null);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const filteredTeams = teams.filter(t => {
    const q = teamSearch.toLowerCase();
    return t.name.toLowerCase().includes(q) || (t.public_id || '').toLowerCase().includes(q);
  });

  const requestMap = Object.fromEntries(myRequests.map(r => [r.team_id, r.status]));

  const activeRequests = myRequests.filter(r => r.status === 'pending' || r.status === 'rejected');
  const pastRequests   = myRequests.filter(r => r.status === 'withdrawn' || r.status === 'approved');
  const [showPastRequests, setShowPastRequests] = useState(false);

  const monoSm = { fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase' };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <div style={{ ...monoSm, color: 'var(--brand-primary)' }}>MEIN KONTO</div>
          {publicId && (
            <span style={{ ...monoSm, fontSize: 9, color: '#849495', background: 'rgba(59,73,75,0.25)', border: '1px solid rgba(59,73,75,0.4)', padding: '2px 8px', letterSpacing: '0.12em' }}>{publicId}</span>
          )}
        </div>
        <h1 style={{ margin: 0 }}>{(form.name || userInfo?.name || 'FAHRER').toUpperCase()}</h1>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid rgba(59,73,75,0.4)' }}>
        {[['profile', 'PROFIL'], ['team', 'TEAM SUCHEN']].map(([key, label]) => {
          const pendingInvites = myInvites.filter(i => i.status === 'pending').length;
          const pendingReqs = activeRequests.filter(r => r.status === 'pending').length;
          const badge = key === 'team' ? pendingInvites + pendingReqs : 0;
          return (
            <button key={key} type="button" onClick={() => setActiveTab(key)}
              style={{ padding: '10px 20px', background: 'transparent', border: 'none', borderBottom: activeTab === key ? '2px solid var(--brand-primary)' : '2px solid transparent', color: activeTab === key ? 'var(--brand-primary)' : '#849495', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s' }}>
              {label}
              {badge > 0 && (
                <span style={{ marginLeft: 6, background: 'var(--brand-secondary)', color: 'var(--accent-contrast)', borderRadius: 10, padding: '1px 6px', fontSize: 8 }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {msg && (
        <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 20 }}>{msg.text}</div>
      )}

      {/* ── PROFIL TAB ── */}
      {activeTab === 'profile' && (
        <>
        {myInvites.filter(i => i.status === 'pending').length > 0 && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('team')}
            onKeyDown={e => e.key === 'Enter' && setActiveTab('team')}
            style={{ cursor: 'pointer', marginBottom: 20, padding: '14px 20px', background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.45)', display: 'flex', alignItems: 'center', gap: 14 }}
          >
            <span style={{ fontSize: 22, flexShrink: 0 }}>📨</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', color: 'var(--brand-secondary)', marginBottom: 4 }}>
                {myInvites.filter(i => i.status === 'pending').length === 1
                  ? 'DU HAST EINE TEAM-EINLADUNG'
                  : `DU HAST ${myInvites.filter(i => i.status === 'pending').length} TEAM-EINLADUNGEN`}
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#e2e2e8' }}>
                Hier klicken um die Einladung anzusehen →
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>

            {/* Left: Photo + Personal */}
            <div className="glass-panel" style={{ padding: '2rem' }}>
              <div style={{ ...monoSm, color: 'var(--brand-primary)', marginBottom: 16 }}>PROFILBILD</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                <div onClick={() => fileRef.current?.click()}
                  style={{ width: 80, height: 80, borderRadius: 4, background: photoPreview ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.08)', border: `1px solid ${photoPreview ? 'rgba(var(--brand-primary-rgb),0.3)' : 'rgba(59,73,75,0.4)'}`, overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                  {photoPreview
                    ? <img src={photoPreview} alt="Profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontFamily: "var(--font-display)", fontSize: '1.5rem', fontWeight: 800, color: 'var(--brand-primary)' }}>{initials}</span>
                  }
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                  ><span style={{ color: '#fff', fontSize: 20 }}>📷</span></div>
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 10, padding: '6px 12px', marginBottom: 6, display: 'block' }} onClick={() => fileRef.current?.click()}>Foto hochladen</button>
                  {photoPreview && <button type="button" className="btn btn-secondary" style={{ fontSize: 10, padding: '4px 12px' }} onClick={() => { setPhotoPreview(null); set('photo_url', ''); }}>Entfernen</button>}
                </div>
              </div>
              <div style={{ ...monoSm, color: 'var(--brand-primary)', marginBottom: 12 }}>PERSÖNLICHE DATEN</div>
              {formField('Vor- und Nachname', 'name', 'text', 'Max Mustermann', true)}
              {formField('Geburtsdatum', 'dob', 'date')}
              {formField('Telefon', 'phone', 'tel', '+49 ...')}
              {formField('Notfallnummer', 'emergency_phone', 'tel', '+49 ...')}
              {formField('Adresse', 'address', 'text', 'Straße, PLZ Ort')}
            </div>

            {/* Right: Race data */}
            <div className="glass-panel" style={{ padding: '2rem' }}>
              <div style={{ ...monoSm, color: 'var(--brand-primary)', marginBottom: 16 }}>RENNDATEN</div>
              {publicId && (
                <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                  <div style={{ ...monoSm, fontSize: 8, color: '#849495', marginBottom: 6 }}>FAHRER-ID</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.06em' }}>{publicId}</div>
                </div>
              )}
              {formField('Lizenznummer', 'license_number', 'text', 'z.B. 12345678')}
              {formField('UCI-ID', 'uci_id', 'text', 'GER19920101')}
              <div style={{ marginTop: 8 }}>
                <div style={{ ...monoSm, color: '#849495', marginBottom: 10 }}>Rennklasse</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {CATS.map(c => {
                    const active = form.category === c;
                    return (
                      <button key={c} type="button" onClick={() => set('category', c)} style={{ padding: '6px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', border: `1px solid ${active ? CAT_C[c] : 'rgba(59,73,75,0.4)'}`, background: active ? `${CAT_C[c]}18` : 'transparent', color: active ? CAT_C[c] : '#849495', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                        {c.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <button type="submit" disabled={saving} className="btn btn-primary" style={{ boxShadow: '0 0 12px rgba(var(--brand-primary-rgb),0.3)', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'SPEICHERN...' : 'PROFIL SPEICHERN'}
            </button>
          </div>
        </form>
        </>
      )}

      {/* ── TEAM SUCHEN TAB ── */}
      {activeTab === 'team' && (
        <div>
          {/* Incoming team invites */}
          {myInvites.filter(i => i.status === 'pending').length > 0 && (
            <div className="glass-panel" style={{ padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid rgba(var(--brand-secondary-rgb),0.25)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ ...monoSm, color: 'var(--brand-secondary)' }}>TEAM-EINLADUNGEN</div>
                <span style={{ background: 'var(--brand-secondary)', color: 'var(--accent-contrast)', fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>
                  {myInvites.filter(i => i.status === 'pending').length}
                </span>
              </div>
              {myInvites.filter(i => i.status === 'pending').map(inv => (
                <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(59,73,75,0.2)', gap: 8 }}>
                  <div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, color: '#e2e2e8', marginBottom: 2 }}>{inv.team_name}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.08em' }}>{inv.team_public_id}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      type="button"
                      disabled={inviteActionLoading === inv.id}
                      onClick={() => handleInviteAction(inv.id, 'accept')}
                      style={{ padding: '7px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(var(--brand-secondary-rgb),0.12)', border: '1px solid rgba(var(--brand-secondary-rgb),0.4)', color: 'var(--brand-secondary)', cursor: 'pointer', opacity: inviteActionLoading === inv.id ? 0.5 : 1 }}
                    >
                      {inviteActionLoading === inv.id ? '...' : 'ANNEHMEN'}
                    </button>
                    <button
                      type="button"
                      disabled={inviteActionLoading === inv.id}
                      onClick={() => handleInviteAction(inv.id, 'decline')}
                      style={{ padding: '7px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: 'transparent', border: '1px solid rgba(59,73,75,0.5)', color: '#849495', cursor: 'pointer', opacity: inviteActionLoading === inv.id ? 0.5 : 1 }}
                    >
                      ABLEHNEN
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Active requests (pending / rejected) */}
          {activeRequests.length > 0 && (
            <div className="glass-panel" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ ...monoSm, color: 'var(--brand-primary)', marginBottom: 14 }}>MEINE ANFRAGEN</div>
              {activeRequests.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(59,73,75,0.2)', gap: 8 }}>
                  <span style={{ fontFamily: "var(--font-display)", fontSize: 14, color: '#e2e2e8', flex: 1 }}>{r.team_name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{
                      ...monoSm, fontSize: 8, padding: '3px 8px',
                      background: r.status === 'pending' ? 'rgba(var(--brand-secondary-rgb),0.1)' : 'rgba(192,0,43,0.1)',
                      color: r.status === 'pending' ? 'var(--brand-secondary)' : '#ff6b6b',
                      border: `1px solid ${r.status === 'pending' ? 'rgba(var(--brand-secondary-rgb),0.3)' : 'rgba(255,107,107,0.3)'}`,
                    }}>
                      {r.status === 'pending' ? 'AUSSTEHEND' : 'ABGELEHNT'}
                    </span>
                    {r.status === 'pending' && (
                      <button
                        type="button"
                        disabled={withdrawLoading === r.id}
                        onClick={() => handleWithdraw(r.id)}
                        style={{ ...monoSm, fontSize: 8, padding: '3px 8px', background: 'transparent', border: '1px solid rgba(255,107,107,0.3)', color: '#ff6b6b', cursor: 'pointer', opacity: withdrawLoading === r.id ? 0.5 : 1 }}
                      >
                        {withdrawLoading === r.id ? '...' : 'ZURÜCKZIEHEN'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Past requests (approved / withdrawn) — collapsible */}
          {pastRequests.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <button
                type="button"
                onClick={() => setShowPastRequests(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, marginBottom: showPastRequests ? 10 : 0 }}
              >
                <span style={{ ...monoSm, fontSize: 8, color: '#849495' }}>FRÜHERE MITGLIEDSCHAFTEN</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495' }}>{showPastRequests ? '▲' : '▼'}</span>
              </button>
              {showPastRequests && (
                <div className="glass-panel" style={{ padding: '1rem 1.5rem' }}>
                  {pastRequests.map(r => (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(59,73,75,0.15)', gap: 8 }}>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#849495', flex: 1 }}>{r.team_name}</span>
                      <span style={{ ...monoSm, fontSize: 8, padding: '2px 7px', background: 'rgba(59,73,75,0.15)', color: '#849495', border: '1px solid rgba(59,73,75,0.3)' }}>
                        {r.status === 'withdrawn' ? 'VERLASSEN' : 'EHEMALS MITGLIED'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Search */}
          <div className="glass-panel" style={{ padding: '1.5rem' }}>
            <div style={{ ...monoSm, color: 'var(--brand-primary)', marginBottom: 14 }}>TEAM SUCHEN</div>
            <input
              type="text"
              placeholder="Teamname eingeben..."
              value={teamSearch}
              onChange={e => setTeamSearch(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "var(--font-display)", fontSize: 14, padding: '10px 14px', outline: 'none', marginBottom: 16, transition: 'border-color 0.2s' }}
              onFocus={e => e.target.style.borderColor = 'var(--brand-primary)'}
              onBlur={e => e.target.style.borderColor = 'rgba(59,73,75,0.4)'}
            />

            {filteredTeams.length === 0 && (
              <div style={{ color: '#849495', fontFamily: "var(--font-display)", fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                {teamSearch ? 'Kein Team gefunden.' : 'Keine Teams verfügbar.'}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredTeams.map(team => {
                const status = requestMap[team.id];
                return (
                  <div key={team.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'rgba(26,28,32,0.5)', border: '1px solid rgba(59,73,75,0.3)' }}>
                    <div>
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, color: '#e2e2e8' }}>{team.name}</span>
                      {team.public_id && <span style={{ marginLeft: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em' }}>{team.public_id}</span>}
                    </div>
                    {status === 'pending' ? (
                      <span style={{ ...monoSm, fontSize: 8, color: 'var(--brand-secondary)', background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', padding: '3px 8px' }}>AUSSTEHEND</span>
                    ) : (
                      <button
                        type="button"
                        disabled={joinLoading === team.id}
                        onClick={() => handleJoinRequest(team.id)}
                        style={{ padding: '6px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: joinLoading === team.id ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.35)', color: 'var(--brand-primary)', cursor: joinLoading === team.id ? 'default' : 'pointer', opacity: joinLoading === team.id ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                        {(status === 'rejected' || status === 'withdrawn') ? 'ERNEUT BEWERBEN' : joinLoading === team.id ? '...' : 'ANFRAGEN'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Demo shell: renders real page components under /t/:slug/* namespace ────────
function DemoApp({ token, user, role, isCoAdmin, isRacing, ownerDriverId, onLogout }) {
  const { slug } = useParams();

  // Non-demo authenticated user visiting a public team page → static public view
  if (!user?.isDemo) return <PublicTeamPage />;

  const noop = () => {};

  return (
    <Routes>
      <Route index element={<Dashboard user={user} />} />
      <Route path="drivers"       element={<Drivers token={token} role={role} isCoAdmin={isCoAdmin} driverId={user?.driverId} teamPublicId={user?.public_id} />} />
      <Route path="events"        element={<Events token={token} role={role} isCoAdmin={isCoAdmin} driverId={user?.driverId} isRacing={isRacing} ownerDriverId={ownerDriverId} driverCategory={user?.category} />} />
      <Route path="events/:id"    element={<EventDetail token={token} role={role} isCoAdmin={isCoAdmin} driverId={user?.driverId} ownerDriverId={ownerDriverId} isRacing={isRacing} />} />
      <Route path="matrix"        element={<RaceMatrix token={token} role={role} isCoAdmin={isCoAdmin} driverId={user?.driverId} />} />
      <Route path="notifications" element={<Notifications token={token} role={role} isCoAdmin={isCoAdmin} isRacing={isRacing} />} />
      <Route path="owner-profile" element={<OwnerProfile token={token} onLogout={onLogout} onTokenRefresh={noop} onProfileUpdate={noop} />} />
      <Route path="profile"       element={<DriverProfile token={token} role={role} isCoAdmin={isCoAdmin} driverId={user?.driverId} onLogout={onLogout} onLeaveTeam={noop} onPhotoUpdate={noop} onTokenRefresh={noop} />} />
      <Route path="*"             element={<Navigate to={`/t/${slug}`} replace />} />
    </Routes>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userInfo, setUserInfo] = useState(() => {
    const stored = localStorage.getItem('userInfo');
    return stored ? JSON.parse(stored) : null;
  });

  // Theme init — apply before first render to avoid flash
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('appTheme') || 'dark';
    document.documentElement.setAttribute('data-theme', saved);
    return saved;
  });

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('appTheme', next);
    // Re-apply branding so inline overrides are set/cleared correctly for new theme
    const cached = localStorage.getItem('teamSettings');
    if (cached) {
      try {
        const s = JSON.parse(cached);
        if (s.colorPrimary) applyBranding(s.colorPrimary, s.colorSecondary || '#feb700');
      } catch {}
    }
  };

  // PWA install prompt ("Zur Homescreen hinzufügen")
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPromptEvt(e);
      if (!localStorage.getItem('pwaInstallDismissed') && token) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [token]);

  // PWA push prompt banner
  const [showPwaPushBanner, setShowPwaPushBanner] = useState(false);
  const [pwaPushLoading, setPwaPushLoading] = useState(false);

  // "Open in app" banner — shown when a deep link is opened in the browser but PWA is installed
  const [showOpenInAppBanner, setShowOpenInAppBanner] = useState(false);
  useEffect(() => {
    if (isPwa()) return; // already in PWA
    if (!token) return;
    // Only show on deep links (not root)
    const path = window.location.pathname;
    if (path === '/' || path === '/login' || path === '/register' || path === '/register-driver') return;
    // Only on mobile
    if (!(/Mobi|Android/i.test(navigator.userAgent))) return;
    setShowOpenInAppBanner(true);
  }, [token]);

  const handleLogin = (token, user) => {
    setToken(token);
    setUserInfo(user);
    localStorage.setItem('token', token);
    localStorage.setItem('userInfo', JSON.stringify(user));
  };

  const handleLogout = () => {
    setToken(null);
    setUserInfo(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
  };

  // Called after a successful password change — stores the fresh token so the
  // current session stays valid (token_version was incremented in the DB).
  const handleTokenRefresh = (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
  };

  // Auto-logout on expired/invalid token (401 from any API call)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const handleLeaveTeam = () => {
    const updated = { ...userInfo, id: null, driverId: null, public_id: null };
    setUserInfo(updated);
    localStorage.setItem('userInfo', JSON.stringify(updated));
  };

  const handlePhotoUpdate = (photoUrl) => {
    const updated = { ...userInfo, photo_url: photoUrl };
    setUserInfo(updated);
    localStorage.setItem('userInfo', JSON.stringify(updated));
  };

  const handleJoinTeam = ({ driverId, id, public_id, token: newToken }) => {
    const updated = { ...userInfo, driverId, id, public_id };
    setUserInfo(updated);
    localStorage.setItem('userInfo', JSON.stringify(updated));
    // If the backend issued a fresh token (with correct team_id), swap it in
    if (newToken) {
      setToken(newToken);
      localStorage.setItem('token', newToken);
    }
  };

  const role = userInfo?.role || null;
  const isCoAdmin = userInfo?.is_co_admin || false;
  const isAdmin = role === 'admin';

  // ── i18n ──────────────────────────────────────────────────────────────────
  const t = useT();
  const { lang } = useLang();

  // ── Email verification banner state ────────────────────────────────────────
  const [resendVerifStatus, setResendVerifStatus] = useState(null); // null | 'sending' | 'sent'

  // On login or app load: fetch real verification status so existing sessions
  // also get the banner without forcing a re-login.
  useEffect(() => {
    if (!token || userInfo?.isDemo) return;
    if (userInfo?.email_verified === true) return;
    axios.get('/api/auth/verification-status', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const verified = !!res.data.email_verified;
        if (verified !== (userInfo?.email_verified ?? null)) {
          const updated = { ...userInfo, email_verified: verified };
          setUserInfo(updated);
          localStorage.setItem('userInfo', JSON.stringify(updated));
        }
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResendVerification = async () => {
    if (resendVerifStatus) return;
    setResendVerifStatus('sending');
    try {
      await axios.post('/api/auth/resend-verification', { lang: lang || 'de' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) {}
    setResendVerifStatus('sent');
  };

  const handleCheckVerification = async () => {
    try {
      const res = await axios.get('/api/auth/verification-status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.email_verified) {
        const updated = { ...userInfo, email_verified: true };
        setUserInfo(updated);
        localStorage.setItem('userInfo', JSON.stringify(updated));
      }
    } catch (_) {}
  };

  // Update manifest, favicon and tab title dynamically after login
  useEffect(() => {
    const publicId  = userInfo?.public_id  || null;
    const teamName  = userInfo?.teamName   || userInfo?.name || null;
    const logoB64   = userInfo?.logo_base64 || null;

    // Manifest
    const manifestUrl = publicId ? `/manifest.json?team=${publicId}` : '/manifest.json';
    let manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = manifestUrl;

    // Favicon — only set team logo when logged in and part of a team
    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    if (logoB64 && publicId) {
      favicon.href = logoB64;
    } else {
      favicon.href = '/assets/logo/mark-favicon.svg';
    }

    // Tab title — only show team name when logged in and part of a team
    document.title = (teamName && publicId) ? teamName : 'Crit Roster';
  }, [userInfo?.public_id, userInfo?.logo_base64, userInfo?.teamName, userInfo?.name]);

  // Fetch owner profile flags once after login (is_racing + owner_driver_id)
  useEffect(() => {
    if (!token || !isAdmin) return;
    axios.get('/api/owner/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const isRacing = !!res.data.is_racing;
        const ownerDriverId = res.data.owner_driver_id || null;
        const changed =
          isRacing !== (userInfo?.is_racing || false) ||
          ownerDriverId !== (userInfo?.ownerDriverId || null);
        if (changed) {
          const updated = { ...userInfo, is_racing: isRacing, ownerDriverId };
          setUserInfo(updated);
          localStorage.setItem('userInfo', JSON.stringify(updated));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin]);

  // Register push notifications after login
  usePushNotifications(token);

  // PWA: show push prompt banner once after login if not yet granted
  useEffect(() => {
    if (!token) return;
    if (!isPwa()) return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') return;
    if (Notification.permission === 'denied') return;
    if (localStorage.getItem('pushNotificationsEnabled') === 'false') return;
    if (localStorage.getItem('pwaPushBannerDismissed') === 'true') return;
    // Small delay so main UI renders first
    const t = setTimeout(() => setShowPwaPushBanner(true), 2000);
    return () => clearTimeout(t);
  }, [token]);

  // Poll driver-status every 20s for active drivers — detect removal or co-admin change
  useEffect(() => {
    if (role !== 'driver' || !userInfo?.driverId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get('/api/auth/driver-status', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.data.driver_id) {
          // Admin removed this driver from the team
          handleLeaveTeam();
        } else if (res.data.token) {
          // Token was refreshed (e.g. team changed or co-admin flag changed) — swap it in silently
          setToken(res.data.token);
          localStorage.setItem('token', res.data.token);
          // Also update userInfo so isCoAdmin reflects the new value immediately
          const newIsCoAdmin = res.data.is_co_admin || false;
          if (newIsCoAdmin !== (userInfo?.is_co_admin || false)) {
            const updated = { ...userInfo, is_co_admin: newIsCoAdmin };
            setUserInfo(updated);
            localStorage.setItem('userInfo', JSON.stringify(updated));
          }
        }
      } catch (_) {}
    }, 20000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role, userInfo?.driverId]);

  // ── Email verification banner (shown across all authenticated states) ──────
  // Use !== true so legacy users (email_verified = undefined) also see the banner immediately,
  // without waiting for the verification-status API call to resolve.
  const emailVerifiedBanner = token && userInfo && userInfo.email_verified !== true && !userInfo.isDemo ? (
    <div style={{
      background: 'rgba(var(--brand-secondary-rgb),0.07)',
      borderBottom: '1px solid rgba(var(--brand-secondary-rgb),0.22)',
      padding: '10px 20px',
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: 15, flexShrink: 0 }}>✉</span>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--brand-secondary)', marginBottom: 2 }}>
          {t('banner_verify_title')}
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'rgba(185,202,203,0.6)', lineHeight: 1.4 }}>
          {t('banner_verify_text')}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        <button onClick={handleResendVerification} disabled={!!resendVerifStatus} style={{
          padding: '6px 14px', background: 'transparent',
          border: '1px solid rgba(var(--brand-secondary-rgb),0.4)',
          color: resendVerifStatus === 'sent' ? 'var(--brand-secondary)' : 'rgba(var(--brand-secondary-rgb),0.8)',
          fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
          cursor: resendVerifStatus ? 'default' : 'pointer',
          opacity: resendVerifStatus === 'sending' ? 0.5 : 1,
        }}>
          {resendVerifStatus === 'sending' ? '...' : resendVerifStatus === 'sent' ? t('banner_verify_sent') : t('banner_verify_resend')}
        </button>
        <button onClick={handleCheckVerification} style={{
          padding: '6px 14px', background: 'transparent',
          border: '1px solid rgba(59,73,75,0.4)',
          color: '#849495',
          fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
          cursor: 'pointer',
        }}>
          {t('banner_verify_check')}
        </button>
      </div>
    </div>
  ) : null;

  // Unauthenticated
  if (!token) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/register-driver" element={<Navigate to="/login" replace />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/t/:slug/*" element={<PublicTeamPage />} />
          <Route path="/join/:inviteToken" element={<JoinTeamPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Driver logged in but not yet linked to a team — limited view
  const isDriverWithoutTeam = role === 'driver' && !userInfo?.driverId && !userInfo?.id;

  if (isDriverWithoutTeam) {
    return (
      <BrowserRouter>
        <div className="app-layout">
          <Navigation userInfo={userInfo} onLogout={handleLogout} onTokenRefresh={handleTokenRefresh} disabledNav={true} theme={theme} onToggleTheme={toggleTheme} />
          <main className="app-main">
            {emailVerifiedBanner}
            <div className="app-content">
              <Routes>
                <Route path="/join/:inviteToken" element={<JoinTeamPage />} />
                <Route path="*" element={<PendingDriverProfile userInfo={userInfo} token={token} onLogout={handleLogout} onJoinTeam={handleJoinTeam} />} />
              </Routes>
            </div>
          </main>
        </div>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <SettingsProvider token={token}>
      <RegistrationsProvider token={token}>
        <div className="app-layout">
          <Navigation userInfo={userInfo} onLogout={handleLogout} onTokenRefresh={handleTokenRefresh} theme={theme} onToggleTheme={toggleTheme} />
          <main className="app-main">
            {emailVerifiedBanner}
            <div className="app-content">
              {/* Onboarding wizard — shown once to new admins until dismissed */}
              {role === 'admin' && userInfo && userInfo.onboarding_completed === false && !userInfo.isDemo && (
                <OnboardingWizard
                  token={token}
                  onDismiss={() => {
                    const updated = { ...userInfo, onboarding_completed: true };
                    setUserInfo(updated);
                    localStorage.setItem('userInfo', JSON.stringify(updated));
                  }}
                />
              )}
              <Routes>
                <Route path="/" element={<Dashboard user={userInfo} />} />
                <Route path="/join/:inviteToken" element={<JoinTeamPage />} />
                <Route path="/:teamSlug" element={<Dashboard user={userInfo} />} />
                <Route path="/drivers" element={<Drivers token={token} role={role} isCoAdmin={isCoAdmin} driverId={userInfo?.driverId} teamPublicId={userInfo?.public_id} />} />
                <Route path="/profile" element={<DriverProfile token={token} role={role} isCoAdmin={isCoAdmin} driverId={userInfo?.driverId} onLogout={handleLogout} onLeaveTeam={handleLeaveTeam} onPhotoUpdate={handlePhotoUpdate} onTokenRefresh={handleTokenRefresh} />} />
                <Route path="/events" element={<Events token={token} role={role} isCoAdmin={isCoAdmin} driverId={userInfo?.driverId} isRacing={userInfo?.is_racing} ownerDriverId={userInfo?.ownerDriverId} driverCategory={userInfo?.category} />} />
                <Route path="/drivers/:id" element={<DriverProfile token={token} role={role} isCoAdmin={isCoAdmin} driverId={userInfo?.driverId} onLogout={handleLogout} onLeaveTeam={handleLeaveTeam} onPhotoUpdate={handlePhotoUpdate} />} />
                <Route path="/events/:id" element={<EventDetail token={token} role={role} isCoAdmin={isCoAdmin} driverId={userInfo?.driverId} ownerDriverId={userInfo?.ownerDriverId} isRacing={userInfo?.is_racing} />} />
                <Route path="/matrix" element={<RaceMatrix token={token} role={role} isCoAdmin={isCoAdmin} driverId={userInfo?.driverId} />} />
                <Route path="/owner-profile" element={role === 'admin' ? <OwnerProfile token={token} onLogout={handleLogout} onTokenRefresh={handleTokenRefresh} onProfileUpdate={(updates) => {
                    const updated = { ...userInfo, ...updates };
                    setUserInfo(updated);
                    localStorage.setItem('userInfo', JSON.stringify(updated));
                  }} /> : <Navigate to="/" />} />
                <Route path="/settings" element={<Settings token={token} userInfo={userInfo} role={role} isCoAdmin={isCoAdmin} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />} />
                <Route path="/notifications" element={<Notifications token={token} role={role} isCoAdmin={isCoAdmin} isRacing={userInfo?.is_racing} />} />
                <Route path="/past-seasons" element={<PastSeasons token={token} />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/t/:slug/*" element={<DemoApp token={token} user={userInfo} role={role} isCoAdmin={isCoAdmin} isRacing={userInfo?.is_racing} ownerDriverId={userInfo?.ownerDriverId} onLogout={handleLogout} />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </div>
          </main>
        </div>

        {/* ── Open in App Banner ── shown in mobile browser when user follows a deep link ── */}
        {showOpenInAppBanner && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            padding: '12px 16px',
            background: 'rgba(0,36,40,0.98)',
            borderBottom: '1px solid rgba(var(--brand-primary-rgb),0.4)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>📱</span>
            <div style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#b9cacb', letterSpacing: '0.05em', lineHeight: 1.4 }}>
              Für die beste Erfahrung öffne den Link in der App.
            </div>
            <a
              href={window.location.href}
              style={{ padding: '8px 14px', background: 'var(--brand-primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textDecoration: 'none', flexShrink: 0 }}
            >
              APP ÖFFNEN
            </a>
            <button
              onClick={() => setShowOpenInAppBanner(false)}
              style={{ background: 'none', border: 'none', color: '#849495', cursor: 'pointer', fontSize: 16, padding: '4px', flexShrink: 0 }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── PWA Install Banner ("Zur Homescreen hinzufügen") ── */}
        {showInstallBanner && installPromptEvt && (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998,
            padding: '14px 20px',
            background: 'rgba(10,12,16,0.97)',
            borderTop: '1px solid rgba(var(--brand-primary-rgb),0.25)',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>📲</span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--brand-primary)', marginBottom: 3 }}>
                ALS APP INSTALLIEREN
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'rgba(185,202,203,0.8)', lineHeight: 1.4 }}>
                Crit Roster zum Homescreen hinzufügen – schneller Zugriff, kein Browser.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={async () => {
                  installPromptEvt.prompt();
                  const { outcome } = await installPromptEvt.userChoice;
                  if (outcome === 'accepted') localStorage.setItem('pwaInstallDismissed', 'true');
                  setShowInstallBanner(false);
                  setInstallPromptEvt(null);
                }}
                style={{ padding: '10px 18px', background: 'var(--brand-primary)', border: 'none', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
              >
                INSTALLIEREN
              </button>
              <button
                onClick={() => {
                  localStorage.setItem('pwaInstallDismissed', 'true');
                  setShowInstallBanner(false);
                }}
                style={{ padding: '10px 14px', background: 'transparent', border: '1px solid rgba(59,73,75,0.5)', color: 'var(--outline)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
              >
                SPÄTER
              </button>
            </div>
          </div>
        )}

        {/* ── PWA Push Prompt Banner ── */}
        {showPwaPushBanner && (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
            padding: '16px 20px',
            background: 'rgba(14,16,20,0.97)',
            borderTop: '1px solid rgba(var(--brand-primary-rgb),0.3)',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>🔔</span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--brand-primary)', marginBottom: 3 }}>
                PUSH-BENACHRICHTIGUNGEN AKTIVIEREN
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'rgba(185,202,203,0.8)', lineHeight: 1.4 }}>
                Damit du keine Event-Erinnerungen verpasst.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={async () => {
                  setPwaPushLoading(true);
                  try {
                    await enablePushNotifications(token);
                  } catch (_) {}
                  finally {
                    setPwaPushLoading(false);
                    setShowPwaPushBanner(false);
                  }
                }}
                disabled={pwaPushLoading}
                style={{ padding: '10px 18px', background: 'var(--brand-primary)', border: 'none', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: pwaPushLoading ? 'not-allowed' : 'pointer', opacity: pwaPushLoading ? 0.6 : 1 }}
              >
                {pwaPushLoading ? '...' : 'AKTIVIEREN'}
              </button>
              <button
                onClick={() => {
                  localStorage.setItem('pwaPushBannerDismissed', 'true');
                  setShowPwaPushBanner(false);
                }}
                style={{ padding: '10px 14px', background: 'transparent', border: '1px solid rgba(59,73,75,0.5)', color: 'var(--outline)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
              >
                SPÄTER
              </button>
            </div>
          </div>
        )}

      </RegistrationsProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

// Wrap App in LangProvider at top level so Login page can also use the toggle
function AppWithLang() {
  return (
    <LangProvider>
      <App />
    </LangProvider>
  );
}

export default AppWithLang;
